/* ESLint errors suppressed for #4183 */
/* eslint-disable css-modules/no-undef-class */
/* eslint css-modules/no-unused-class: [2, { markAsUsed: ['giftCard'] }] */ /* This class is used in the other uses of stylePickerDropdown.scss */
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { connect } from 'react-redux';
import ExecutionEnvironment from 'exenv';
import template from 'lodash.template';
import debounce from 'lodash.debounce';

import AriaLive from 'components/common/AriaLive';
import { capitalize, head } from 'helpers/lodashReplacement';
import { trackEvent } from 'helpers/analytics';
import { onEvent } from 'helpers/EventHelpers';
import { setRecommendedFit } from 'actions/productdetail/sizingChooser';
import { fireOnDemandEvent } from 'actions/productdetail/onDemandSizing';
import { fireSizingImpression, productSizeChanged, validateDimensions } from 'actions/productDetail';
import { openPopup } from 'helpers';
import RecommendedSizing from 'components/productdetail/stylepicker/RecommendedSizing';
import PageContent from 'components/landing/PageContent';
import ProductUtils from 'helpers/ProductUtils';
import marketplace from 'cfg/marketplace.json';
import { isDesktop } from 'helpers/ClientUtils';

import css from 'styles/components/productdetail/stylePickerDropdown.scss';

const VALID_DISPLAY_GENDERS = ['Men', 'Mens', 'Women', 'Womens'];
const NORMALIZED_GENDER = {
  'Men': 'Men',
  'Mens': 'Men',
  'Women': 'Women',
  'Womens': 'Women'
};

function makePlaceholder(sizingPlaceholder, gender, dimensionName) {
  const compiled = template(sizingPlaceholder);
  const placeholder = compiled({ gender, dimensionName: capitalize(dimensionName) });
  return placeholder.replace(/\s+/g, ' ');
}

function makeSizingOption(option, sizingPredictionId) {
  const { id, value } = option;
  // The markup needs to be returned separately otherwise it causes a re-rendering error:
  // https://github01.zappos.net/mweb/marty/issues/3175
  return (sizingPredictionId && sizingPredictionId === id) ?
    <option key={id} value={id}>{`${value} - Recommended Size`}</option> :
    <option key={id} value={id}>{value}</option>;
}

function makeSingleValue(option, dimensionId, dimensionName, testId) {
  // option _could_ be undefined if the product is incorrectly setup.  Don't blow up if it setup wrong.
  option = option || { value: '', id: '' };
  return (
    <div className={css.styleChooserText} data-test-id={testId(`${dimensionId}-singleValue`)}>
      <input type="hidden" name={dimensionId} value={option.id}/>
      <span className={css.dimensionLabel}>{capitalize(dimensionName)}: </span>
      {option.value}
    </div>
  );
}

const SymphonySizeBuyBoxContent = ({ sizeSymphonyContent }) => {
  if (sizeSymphonyContent) {
    return (
      <PageContent
        containerDataId="pdp-buybox"
        additionalClassName={css.symphonyBuyBoxPageContentSize}
        slotDetails={sizeSymphonyContent}/>
    );
  }
  return null;
};

export class SizingChooser extends Component {
  state = {
    isOnDemandSizingModalOpen: false,
    isDesktopView: null
  };

  componentDidMount() {
    onEvent(window, 'resize', this.handleResize, null, this);
    this.handleResize();
  }

  componentDidUpdate(prevProps) {
    const { product: { isOnDemandEligible, sizingPredictionId, sizingPredictionValue } } = this.props;
    const { fireSizingImpression, product: { sizingPredictionId: prevSizingPredictionId, isOnDemandEligible: prevIsOnDemandEligible } } = prevProps;
    if (sizingPredictionId !== prevSizingPredictionId && sizingPredictionValue) {
      fireSizingImpression('DIRECT_PREDICTION', { id: sizingPredictionId, value: sizingPredictionValue });
    } else if (isOnDemandEligible && isOnDemandEligible !== prevIsOnDemandEligible) {
      fireSizingImpression('ON_DEMAND_ELIGIBLE');
    }
  }

  handleResize = debounce(() => {
    // isDesktop doesnt work serverside, will be null serverside, or bool clientside
    this.setState({ isDesktopView: ExecutionEnvironment.canUseDOM ? isDesktop(marketplace) : null });
  }, 100);

  handleCalculateSizeClick = () => {
    const { fireOnDemandEvent } = this.props;
    fireOnDemandEvent('start');
    this.setState({ isOnDemandSizingModalOpen: true });
  };

  handleCloseModalClick = () => {
    this.setState({ isOnDemandSizingModalOpen: false });
  };

  handleSetRecommendedFit = predictedSize => {
    const { product, setRecommendedFit, productSizeChanged, fireSizingImpression } = this.props;
    this.setState({ isOnDemandSizingModalOpen: false });
    setRecommendedFit(predictedSize);

    const { selectedSizing, detail: { productId, sizing: { dimensions } } } = product;
    const newSelectedSizing = { ...selectedSizing };

    const sizeDimension = dimensions.find(dim => dim.name === 'size');
    const dimValues = sizeDimension.units[0].values;
    const sizeObj = dimValues.find(({ value }) => value === predictedSize);

    if (sizeObj) {
      newSelectedSizing[`d${sizeDimension.id}`] = sizeObj.id;
      productSizeChanged(newSelectedSizing);
      validateDimensions();
    }

    fireSizingImpression('ON_DEMAND_PREDICTION', sizeObj);
    trackEvent('TE_PDP_SIZING', `${productId}:SizeSelected:${predictedSize}`);
    trackEvent('TE_PDP_ONDEMAND_SIZING', `${productId}:SizeSuggested:${predictedSize}`);
  };

  isNeededValuesSelected(selectedVal, options, isSelectSizeTooltipVisible) {
    return !selectedVal && options.length > 1 && isSelectSizeTooltipVisible;
  }

  makeLabelMessaging(showSizeGender, gender, dimensionName, needMoreSelections, isSelectSizeTooltipHighlighted) {
    const { testId } = this.context;
    if (needMoreSelections) {
      const text = ProductUtils.buildSizeMessagingText(dimensionName);
      return <>
        <span className={css.needSelectionLabel} data-test-id={testId('selectionError')}>{text}</span>
        { isSelectSizeTooltipHighlighted && <AriaLive> {text} </AriaLive> }
      </>;
    } else if ((showSizeGender || dimensionName === 'size') && gender !== '') {
      return `${gender} ${dimensionName}`;
    }
    return dimensionName;
  }

  render() {
    const { testId } = this.context;
    const {
      genders,
      sizing,
      selectedSizing,
      dimensionValidation,
      sizingPlaceholder,
      product,
      productType,
      onSizeChange,
      showSizeGender,
      showSizeChartLink,
      isSelectSizeTooltipVisible,
      isSelectSizeTooltipHighlighted,
      sizingPredictionId,
      isOnDemandEligible,
      onDemandSizing,
      hasRecommendedSizing,
      hydraPdpOdspLinkChange,
      location,
      sizeSymphonyContent
    } = this.props;

    const { isOnDemandSizingModalOpen, isDesktopView } = this.state;
    const { predictedSize } = onDemandSizing;

    const gender = genders.length === 1 && VALID_DISPLAY_GENDERS.includes(head(genders)) ? head(genders).replace(/s?$/, '\'s') : '';
    const onDemandSizingGender = genders.length === 1 ? NORMALIZED_GENDER[head(genders)] : null;
    const sizeDimension = sizing.dimensions.find(dimension => dimension.name === 'size');
    const showRecommendedSizing = hasRecommendedSizing && sizeDimension && ProductUtils.isShoeType(productType) && gender && (isOnDemandEligible !== false);
    let selectedSize;
    return (
      <>
        { isSelectSizeTooltipHighlighted && <AriaLive> Selection is incomplete. </AriaLive> }
        <div className={css.stackableStyleChooserSections}>
          {sizing.dimensions.map((dimension, index) => {
            const dimensionId = `d${dimension.id}`;
            const dimensionName = dimension.name;
            const isSizeDimension = dimensionName === 'size';
            const options = sizing.allUnits[index].values;
            const isIncomplete = dimensionValidation[dimensionId];
            let selectedVal = selectedSizing[dimensionId];
            if (options.length === 1) {
              selectedVal = head(options).id;
            }

            const onDemandSizingPredictionSize = options.find(option => option.value === predictedSize);
            const onDemandSizingPredictionId = onDemandSizingPredictionSize ? onDemandSizingPredictionSize.id : null;
            const pdpDimensionSelectId = `pdp-${dimensionName}-select`;
            const needMoreSelections = this.isNeededValuesSelected(selectedVal, options, isSelectSizeTooltipVisible);

            if (isSizeDimension && sizing.valueIdToName && selectedVal) {
              selectedSize = sizing.valueIdToName[selectedVal]?.value;
            }

            return (
              <Fragment key={dimensionName}>

                {/* Mobile specific size elements START */}
                {isDesktopView === false && isSizeDimension && (
                  <div className={css.mobileOnlyBlock}>
                    <SymphonySizeBuyBoxContent sizeSymphonyContent={sizeSymphonyContent}/>
                    {hydraPdpOdspLinkChange && showRecommendedSizing &&
                      <RecommendedSizing
                        onOpenModal={this.handleCalculateSizeClick}
                        onCloseModal={this.handleCloseModalClick}
                        product={product}
                        gender={onDemandSizingGender}
                        handleSetRecommendedFit={this.handleSetRecommendedFit}
                        selectedSize={selectedSize}
                        location={location}
                        handleCalculateSizeClick={this.handleCalculateSizeClick}
                        isOnDemandSizingModalOpen={isOnDemandSizingModalOpen}
                        hydraPdpOdspLinkChange={hydraPdpOdspLinkChange}
                        isDesktopView={isDesktopView}/>
                    }
                  </div>
                )}
                {/* Mobile specific size elements END */}

                <div
                  className={cn(
                    css.styleChooserSection,
                    { [css.invalidDimensionControl]: isIncomplete },
                    { [css.onlyOneChooserSection]: sizing.dimensions.length === 1 })}
                  data-test-id={testId(`${dimensionId}-sizingChooser`)}>
                  <label className={cn({ [css.singleVal]: options.length <= 1 })} htmlFor={pdpDimensionSelectId}>
                    {this.makeLabelMessaging(showSizeGender, gender, dimensionName, needMoreSelections, isSelectSizeTooltipHighlighted)}
                    {showSizeChartLink && isSizeDimension && ProductUtils.isShoeType(productType) && (
                      <span className={css.styleChooserSizeChart}>
                        <span> (<a
                          href="/cs/mmf-popop.zml"
                          target="_blank"
                          onClick={openPopup}
                          data-popup-options="width=1000,height=800"
                          data-track-action="Product-Page"
                          data-track-label="Tabs"
                          data-track-value="Size-Chart">Size Chart</a>)</span>
                      </span>
                    )}
                  </label>

                  {/* Desktop specific size elements START */}
                  {isDesktopView === true && isSizeDimension &&
                    <>
                      <SymphonySizeBuyBoxContent sizeSymphonyContent={sizeSymphonyContent}/>
                      {showRecommendedSizing && hydraPdpOdspLinkChange &&
                        <RecommendedSizing
                          onOpenModal={this.handleCalculateSizeClick}
                          onCloseModal={this.handleCloseModalClick}
                          product={product}
                          gender={onDemandSizingGender}
                          handleSetRecommendedFit={this.handleSetRecommendedFit}
                          selectedSize={selectedSize}
                          location={location}
                          handleCalculateSizeClick={this.handleCalculateSizeClick}
                          isOnDemandSizingModalOpen={isOnDemandSizingModalOpen}
                          hydraPdpOdspLinkChange={hydraPdpOdspLinkChange}
                          isDesktopView={isDesktopView}/>
                      }
                    </>
                  }
                  {/* Desktop specific size elements END */}

                  {options.length > 1 ? (
                    <div className={css.styleChooserControlWrapper}>
                      <select
                        id={pdpDimensionSelectId}
                        name={dimensionId}
                        className={cn(css.styleChooserControl, {
                          [css.highlighted]: isSelectSizeTooltipHighlighted,
                          [css.recommendedSizeSelect]: selectedVal === sizingPredictionId || selectedVal === onDemandSizingPredictionId,
                          [css.needSelection]: needMoreSelections
                        })}
                        value={selectedVal || sizingPredictionId || ''}
                        onChange={onSizeChange.bind(this, options)}
                        data-track-label={dimensionName}>
                        <option value="">{makePlaceholder(sizingPlaceholder, gender, dimensionName)}</option>
                        {options.map(option =>
                          makeSizingOption(option, sizingPredictionId)
                        )}
                      </select>
                    </div>
                  ) : makeSingleValue(options[0], dimensionId, dimensionName, testId)}
                </div>
              </Fragment>
            );
          })}
        </div>
        { showRecommendedSizing && ((hydraPdpOdspLinkChange && isDesktopView === false) || !hydraPdpOdspLinkChange) &&
          <RecommendedSizing
            onOpenModal={this.handleCalculateSizeClick}
            onCloseModal={this.handleCloseModalClick}
            product={product}
            gender={onDemandSizingGender}
            handleSetRecommendedFit={this.handleSetRecommendedFit}
            selectedSize={selectedSize}
            location={location}
            handleCalculateSizeClick={this.handleCalculateSizeClick}
            isOnDemandSizingModalOpen={isOnDemandSizingModalOpen}/>
        }
      </>
    );
  }
}

SizingChooser.contextTypes = {
  testId: PropTypes.func
};

SizingChooser.propTypes = {
  genders: PropTypes.arrayOf(PropTypes.string),
  sizing: PropTypes.object,
  selectedSizing: PropTypes.object,
  dimensionValidation: PropTypes.object,
  sizingPlaceholder: PropTypes.string,
  productType: PropTypes.string,
  onSizeChange: PropTypes.func,
  showSizeGender: PropTypes.bool,
  showSizeChartLink: PropTypes.bool,
  isSelectSizeTooltipVisible: PropTypes.bool
};

const mapStateToProps = state => {
  const { product, sizingChooser: { onDemandSizing } } = state;
  return {
    product,
    onDemandSizing
  };
};

export default connect(mapStateToProps, {
  fireSizingImpression,
  fireOnDemandEvent,
  setRecommendedFit,
  productSizeChanged,
  validateDimensions
})(SizingChooser);
/* eslint-enable */
